import {Svg, Path} from 'react-native-svg';
import {useCallback, useEffect} from 'react';
import {useToast} from 'react-native-toast-notifications';
import {useDispatch, useSelector} from 'react-redux';
import {useWindowDimensions, StyleSheet, View, Text, ActivityIndicator} from 'react-native';
import {useModalCreateFolder} from 'interface/hooks/useModalCreateFolder';
import {useModalStorageUpsell} from 'interface/hooks/useModalStorageUpsell';
import {useNavigate} from 'extensions/navigation';
import {FileList} from 'interface/stacks/FileList';
import {FileListItemLayout} from 'interface/stacks/FileListItem';
import {FolderNavigator} from 'interface/stacks/FolderNavigator';
import {IconButton} from 'interface/base/IconButton';
import {vectors, colors} from 'config/styles';
import {theme} from 'config/themes';
import {copy, load, move, restore} from 'utils/mediafire';
import {isUpsellDeniedPlatform} from 'utils/platform';
import {selectors} from 'store/slices/files';

import * as app from 'store/slices/app';
import * as filesEffects from 'interface/hooks/effects/files';
import {FilesErrors} from 'store/slices/files/types';

export function FolderPicker() {
  const toast = useToast();
  const screen = useWindowDimensions();
  const header = useSelector(app.selectors.getHeader);
  const limits = useSelector(app.selectors.getLimits);
  const items = useSelector(selectors.getItems);
  const pick = useSelector(selectors.getPick);
  const action = pick ? pick.action : 'copy';
  const destination = pick ? pick.destination : 'myfiles';
  const destName = items[destination] ? items[destination].name : 'My Files';
  const sourceName = !pick || pick.ids.length > 1 ? 'items' : 'item';
  const actionName = action.slice(0,1).toUpperCase() + action.slice(1);
  const folder = useSelector(selectors.getItem(destination));
  const view = useSelector(selectors.getView(destination));
  const colorPrimary = header ? header.colors.primary : colors.primary.brand;
  const sizeExpected = folder ? folder.folders : 0;
  const sizeActual = view ? view.folders.length : 0;
  const isLoading = !folder || sizeActual === 0 && sizeExpected > 0;
  const isEmpty = !isLoading && view && sizeExpected === 0;
  const isMinimal = screen.width < 500;

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const modalCreateFolder = useModalCreateFolder(destination);
  const [showUpsellStorage] = useModalStorageUpsell('manage');
  const doPick = useCallback(filesEffects.pickSubmit(pick, navigate, dispatch), [pick]);
  const exit = useCallback(filesEffects.pick(dispatch, null), []);
  const submit = useCallback(async () => {
    if (pick) {
      let status: any;
      if (action === 'copy' || action === 'save') {
        toast.show(`Copied ${pick.ids.length} ${sourceName} to ${destName}`);
        status = await copy(dispatch, pick.ids, destination);
      } else if (action === 'move') {
        toast.show(`Moved ${pick.ids.length} ${sourceName} to ${destName}`);
        status = await move(dispatch, pick.ids, destination);
      } else if (action === 'restore') {
        toast.show(`Restored ${pick.ids.length} ${sourceName} to ${destName}`);
        status = await restore(dispatch, pick.ids, destination);
      }
      const error = status?.error;
      if (error) {
        if (error === FilesErrors.StorageLimitExceeded) {
          if (isUpsellDeniedPlatform()) {
            toast.show(`Not enough storage space to complete this action`, {type: 'danger'});
          } else {
            let totalSize = 0;
            pick.ids.forEach(id => (totalSize += items[id].size));
            showUpsellStorage(totalSize, limits.storage.total);
          }
        } else if (error === FilesErrors.AccessDenied) {
          toast.show(`You do not have write permissions`, {type: 'danger'});
        } else {
          toast.show(`There was a problem attempting this action (code ${error})`, {type: 'danger'});
        }
      } else {
      }
    }
    filesEffects.pickClear(dispatch);
  }, [pick, items]);

  useEffect(() => {
    if (pick) {
      load(navigate, dispatch, 'myfiles', true);
    }
  }, [pick]);

  return pick ? (
    <View style={styles.root} onPointerDown={() => exit()}>
      <View style={styles.inner} onPointerDown={(e) => e.stopPropagation()}>
        <View style={styles.content}>
          <FolderNavigator picker id={destination}/>
            <View style={styles.list}>
              {!isEmpty &&
                <FileList
                  view={view}
                  items={items}
                  layout={FileListItemLayout.ListNormal}
                  onOpen={doPick}
                  options={{
                    hideFiles: true,
                    hideIcons: true,
                    hideHeader: true,
                    hideGutter: true,
                    disableDrag: true,
                    disableFocus: true,
                    disableSelect: true,
                    disablePullToRefresh: true,
                  }}
                />
              }
              {isEmpty &&
                <View style={styles.empty}>
                  <Text style={styles.emptyText}>
                    {`${actionName} ${sourceName} here`}
                  </Text>
                </View>
              }
              {isLoading &&
                <View style={styles.loading}>
                  <ActivityIndicator color={colorPrimary} size="small"/>
                </View>
              }
            </View>
        </View>
        <View style={styles.actions}>
          <IconButton
            mode="primary"
            onPress={submit}
            title={`Start ${action}`}
            text={`${action} here`.toUpperCase()}
          />
          <View style={styles.spacerH}/>
          <IconButton
            mode="cancel"
            onPress={exit}
            title={`Cancel ${action}`}
            text={`Cancel`.toUpperCase()}
          />
          <View style={styles.spacerFill}/>
          <IconButton
            onPress={modalCreateFolder.open}
            title={`Create new folder`}
            text={!isMinimal ? `NEW FOLDER` : null}
            buttonStyle={styles.button}
            buttonHoverStyle={styles.buttonHover}
            textStyle={styles.buttonText}
            icon={
              <Svg viewBox="0 0 20 16" width={20} height={16}>
                <Path d={vectors.icons.folderAdd} fill={colors.neutral.mid} fillOpacity={1}/>
              </Svg>
            }
          />
        </View>
      </View>
    </View>
  ) : null;
}

export const styles = StyleSheet.create({
  root: {
    position: 'absolute',
    backgroundColor: theme.dialog.overlay,
    justifyContent: 'center',
    alignContent: 'center',
    alignItems: 'center',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  },
  inner: {
    borderRadius: 3,
    height: 480,
    minWidth: 320,
    maxWidth: 640,
    backgroundColor: theme.transfers.fill,
    shadowOffset: {width: 0, height: 3},
    shadowColor: 'rgba(0,0,0,0.3)',
    shadowRadius: 3,
    elevation: 1,
  },
  actions: {
    height: 64,
    flexDirection: 'row-reverse',
    padding: 10,
    paddingLeft: 6,
    borderTopWidth: 2,
    borderColor: theme.dialog.border,
    backgroundColor: theme.dialog.fill,
  },
  button: {
    paddingHorizontal: 8,
    alignItems: 'center',
    flexDirection: 'row',
    minWidth: 32,
  },
  buttonHover: {
    backgroundColor: theme.button.cancel.hover,
  },
  buttonText: {
    color: theme.button.cancel.text,
  },
  content: {
    flex: 1,
    margin: 0,
  },
  list: {
    height: 360,
  },
  empty: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyText: {
    color: theme.button.primary.text,
    fontSize: 14,
  },
  loading: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  spacerH: {
    width: 6,
  },
  spacerFill: {
    flex: 1,
  },
});
